export type GallerySortBy = "date";
export type GalleryDateType = "published" | "created" | "modified";
export type GallerySortOrder = "ASC" | "DESC";

export type GallerySortValue = {
  sortBy: GallerySortBy;
  /** Which date column the sort orders on. */
  dateType: GalleryDateType;
  order: GallerySortOrder;
};

export type GalleryFilterValue = {
  /** "YYYY-MM-DD" or "" */
  fromDate: string;
  /** "YYYY-MM-DD" or "" */
  toDate: string;
  author: string[];
};

export const GALLERY_SORT_BY: Record<string, GallerySortBy> = {
  DATE: "date",
};

export const GALLERY_DATE_TYPES: Record<string, GalleryDateType> = {
  PUBLISHED: "published",
  CREATED: "created",
  MODIFIED: "modified",
};

export const SORT_ORDER_DESC: GallerySortOrder = "DESC";
export const SORT_ORDER_ASC: GallerySortOrder = "ASC";

export const DEFAULT_GALLERY_SORT: GallerySortValue = {
  sortBy: "date",
  dateType: "published",
  order: "DESC",
};

export const DEFAULT_GALLERY_FILTER: GalleryFilterValue = {
  fromDate: "",
  toDate: "",
  author: [],
};

export const hasActiveFilter = (filters?: GalleryFilterValue | null): boolean =>
  Boolean(filters && (filters.fromDate || filters.toDate || filters.author?.length));

/** Chip and dialog labels for each date column. */
export const GALLERY_DATE_TYPE_LABEL_KEYS: Record<GalleryDateType, string> = {
  published: "published_date",
  created: "created_date",
  modified: "last_modified",
};

/** Chip and dialog labels for each direction. */
export const GALLERY_ORDER_LABEL_KEYS: Record<GallerySortOrder, string> = {
  DESC: "latest_first",
  ASC: "oldest_first",
};

export type GalleryAppliedChipKind = "dateRange" | "author" | "sort";

export type GalleryAppliedChip = {
  /** Unique within its row; also the `data-testid` suffix. */
  id: string;
  kind: GalleryAppliedChipKind;
  /**
   * i18n key for the group prefix ("Date Range", "Author"). Omitted where the row's own
   * heading already names the group, as it does for the single Sort chip.
   */
  groupKey?: string;
  /** i18n keys for the value - each translated, then joined by the row. */
  valueKeys?: string[];
  /** Literal value text that must NOT be translated: author names, dates. */
  valueText?: string;
  /** Which value the cross drops. Only set on the per-value author chips. */
  value?: string;
};

export const buildFilterChips = (filters?: GalleryFilterValue | null): GalleryAppliedChip[] => {
  const chips: GalleryAppliedChip[] = [];

  if (filters?.fromDate || filters?.toDate) {
    const { fromDate: from = "", toDate: to = "" } = filters;
    chips.push({
      id: "date",
      kind: "dateRange",
      groupKey: "date_range",
      valueText: from && to ? `${from} - ${to}` : from || to,
    });
  }

  (filters?.author || []).forEach((value) => {
    chips.push({
      id: `author-${value}`,
      kind: "author",
      groupKey: "author",
      value,
      valueText: value,
    });
  });

  return chips;
};

export const buildSortChip = (sort?: GallerySortValue | null): GalleryAppliedChip | null =>
  sort
    ? {
        id: "sort",
        kind: "sort",
        valueKeys: [
          GALLERY_DATE_TYPE_LABEL_KEYS[sort.dateType],
          GALLERY_ORDER_LABEL_KEYS[sort.order],
        ],
      }
    : null;

export const countActiveFilters = (filters?: GalleryFilterValue | null): number => {
  if (!filters) return 0;
  return (filters.fromDate || filters.toDate ? 1 : 0) + (filters.author?.length ? 1 : 0);
};

type SortVariables = {
  sort?: GallerySortOrder;
  sortOptions?: { date?: GallerySortOrder; date_type?: GalleryDateType };
};

export const buildSortVariables = (sort?: GallerySortValue | null): SortVariables => {
  if (!sort) return {};
  return {
    sort: sort.order,
    sortOptions: { date: sort.order, date_type: sort.dateType },
  };
};

type FilterVariables = {
  dateFilter?: { from: string; to: string };
  created_by?: string[];
};

export const buildFilterVariables = (filters?: GalleryFilterValue | null): FilterVariables => {
  if (!filters) return {};
  return {
    ...(filters.fromDate || filters.toDate
      ? { dateFilter: { from: filters.fromDate, to: filters.toDate } }
      : {}),
    ...(filters.author?.length ? { created_by: filters.author } : {}),
  };
};

export const buildAdvancedVariables = (
  sort?: GallerySortValue | null,
  filters?: GalleryFilterValue | null,
): SortVariables & FilterVariables => ({
  ...buildSortVariables(sort),
  ...buildFilterVariables(filters),
});

export const hasAdvancedQuery = (
  sort?: GallerySortValue | null,
  filters?: GalleryFilterValue | null,
): boolean => Boolean(sort) || hasActiveFilter(filters);
